// ==================== CONFIGURAZIONI MACCHINE SPELAFILI ====================
//
// Registry estensibile: per aggiungere una nuova macchina, basta aggiungere
// un nuovo entry all'oggetto MACHINES con le proprie sezioni e parametri.
// Il componente UI li renderizzerà automaticamente.

import { TERMINAL_DATA } from './terminalConstants';

/**
 * Calcola se l'INNER va abilitato (almeno un terminale ha peel > 0)
 */
export const needsInnerStripping = (cable, terminalData = TERMINAL_DATA) => {
    if (!cable.poli || !Array.isArray(cable.poli)) return false;
    return cable.poli.some(polo => {
        const termA = terminalData[polo.terminaleA];
        const termB = terminalData[polo.terminaleB];
        return (termA && termA.peel > 0) || (termB && termB.peel > 0);
    });
};

/**
 * Registry delle macchine spelafili.
 * Ogni macchina definisce sezioni con parametri calcolabili o manuali.
 */
export const MACHINES = {
    'EW-05': {
        id: 'EW-05',
        name: 'EW-05',
        description: 'Automatic wire cutting stripping and twisting machine',
        /**
         * Genera le sezioni e parametri per un dato cavo multipolare.
         * @param {Object} cable - Il cavo multipolare con .poli[], .lunghezza
         * @param {Object} terminalData - TERMINAL_DATA o override personalizzato
         * @returns {Array} sezioni con parametri calcolati
         */
        computeSections: (cable, terminalData = TERMINAL_DATA) => {
            // Costanti specifiche per la macchina EW-05
            const TWO_PASS_THRESHOLD = 150;
            const INNER_RETRACT_FACTOR = 0.3;
            const OUTER_RETRACT_FACTOR = 0.2;
            const OUTER_RETRACT_PERCENT = (OUTER_RETRACT_FACTOR * 100).toFixed(0);
            const INNER_RETRACT_PERCENT = (INNER_RETRACT_FACTOR * 100).toFixed(0);

            const poli = cable.poli || [];
            if (poli.length === 0) return [];

            // Calcoli lato B (Left sulla macchina)
            // Strip L = lunghezzaB dei fili (quanto sporgono dalla guaina)
            const lunghezzeB = poli.map(p => p.lunghezzaB || 0);
            const targetStripL = Math.max(...lunghezzeB);

            // Calcoli lato A (Right sulla macchina)
            const lunghezzeA = poli.map(p => p.lunghezzaA || 0);
            const targetStripR = Math.max(...lunghezzeA);

            // Logica 2 passate: se strip > 150mm
            const needsSplitL = targetStripL > TWO_PASS_THRESHOLD;
            const needsSplitR = targetStripR > TWO_PASS_THRESHOLD;
            const hasTwoPass = needsSplitL || needsSplitR;

            // INNER: controlla se serve spelatura interna
            const innerEnabled = needsInnerStripping(cable, terminalData);

            // Calcola parametri INNER (Strip = len, Pull-off = peel)
            let innerStripL = 0, innerPullCombineL = 0;
            let innerStripR = 0, innerPullCombineR = 0;

            if (innerEnabled) {
                poli.forEach(polo => {
                    const termA = terminalData[polo.terminaleA];
                    const termB = terminalData[polo.terminaleB];
                    // Lato B = Left
                    if (termB && termB.peel > 0) {
                        innerStripL = Math.max(innerStripL, termB.len || 0);
                        innerPullCombineL = Math.max(innerPullCombineL, termB.peel || 0);
                    }
                    // Lato A = Right
                    if (termA && termA.peel > 0) {
                        innerStripR = Math.max(innerStripR, termA.len || 0);
                        innerPullCombineR = Math.max(innerPullCombineR, termA.peel || 0);
                    }
                });
            }

            // Sezione rappresentativa per blade value INNER
            const sezioni = [...new Set(poli.map(p => p.sezione))];
            const maxSezione = Math.max(...sezioni);

            const sections = [];

            // === GENERAL: Full Length ===
            sections.push({
                id: 'general',
                label: 'Generale',
                labelKey: 'machineConfig.EW05.sections.general',
                parameters: [
                    {
                        key: 'fullLength',
                        label: 'Full Length',
                        value: cable.lunghezza,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.general.lenght'
                    },
                    {
                        key: 'bladeValue',
                        label: 'Blade Value',
                        value: null,
                        unit: 'mm',
                        manual: true,
                        tooltipKey: 'machineConfig.EW05.general.bladeValue'
                    },
                    {
                        key: 'retract',
                        label: 'Retract',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: OUTER_RETRACT_FACTOR,
                        tooltipKey: 'machineConfig.EW05.general.retractWithPercent',
                        tooltipParams: { percent: OUTER_RETRACT_PERCENT }
                    },
                    {
                        key: 'rollerLGap',
                        label: 'Roller L. Gap',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: 0.7,
                        roundInteger: true,
                        tooltipKey: 'machineConfig.EW05.general.rollerLGap'
                    },
                    {
                        key: 'rollerRGap',
                        label: 'Roller R. Gap',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: 0.7,
                        roundInteger: true,
                        tooltipKey: 'machineConfig.EW05.general.rollerRGap'
                    },
                ]
            });

            // === OUTER 1: Guaina esterna (1° passata) ===
            // Se serve 2 passate su un lato, cappiamo a 150mm. Altrimenti target intero.
            sections.push({
                id: 'outer1',
                label: 'OUTER',
                sublabel: hasTwoPass ? 'Sguainatura guaina esterna - 1° passata' : 'Sguainatura guaina esterna',
                sublabelKey: hasTwoPass
                    ? 'machineConfig.EW05.sections.outer1TwoPass'
                    : 'machineConfig.EW05.sections.outer1Single',
                parameters: [
                    {
                        key: 'stripL',
                        label: 'Strip L',
                        value: needsSplitL ? TWO_PASS_THRESHOLD : targetStripL,
                        unit: 'mm',
                        tooltipKey: needsSplitL
                            ? 'machineConfig.EW05.outer1.stripLMax'
                            : 'machineConfig.EW05.outer1.stripL',
                        tooltipParams: needsSplitL ? { max: TWO_PASS_THRESHOLD } : undefined
                    },
                    {
                        key: 'pullOffL',
                        label: 'Pull-off',
                        value: needsSplitL ? (TWO_PASS_THRESHOLD + 5) : (targetStripL + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: 'machineConfig.EW05.outer1.pullOffComplete'
                    },
                    {
                        key: 'stripR',
                        label: 'Strip R',
                        value: needsSplitR ? TWO_PASS_THRESHOLD : targetStripR,
                        unit: 'mm',
                        tooltipKey: needsSplitR
                            ? 'machineConfig.EW05.outer1.stripRMax'
                            : 'machineConfig.EW05.outer1.stripR',
                        tooltipParams: needsSplitR ? { max: TWO_PASS_THRESHOLD } : undefined
                    },
                    {
                        key: 'pullOffR',
                        label: 'Pull-off',
                        value: needsSplitR ? (TWO_PASS_THRESHOLD + 5) : (targetStripR + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: 'machineConfig.EW05.outer1.pullOffRComplete'
                    },
                ]
            });

            // === INNER: Spelatura fili interni (opzionale) ===
            sections.push({
                id: 'inner',
                label: 'INNER',
                sublabel: 'Spelatura cavi interni',
                sublabelKey: 'machineConfig.EW05.sections.inner',
                enabled: innerEnabled,
                parameters: innerEnabled ? [
                    {
                        key: 'stripL',
                        label: 'Strip L',
                        value: innerStripL,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.stripL'
                    },
                    {
                        key: 'pullOffL',
                        label: 'Pull-off',
                        value: innerPullCombineL,
                        unit: 'mm',
                        tooltipKey: innerPullCombineL >= innerStripL
                            ? 'machineConfig.EW05.inner.pullOffLComplete'
                            : 'machineConfig.EW05.inner.pullOffLPartial'
                    },
                    {
                        key: 'bladeValue',
                        label: 'Blade Value',
                        value: maxSezione,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.bladeValue'
                    },
                    {
                        key: 'retract',
                        label: 'Retract',
                        value: Math.round(maxSezione * INNER_RETRACT_FACTOR * 100) / 100,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.retractWithPercent',
                        tooltipParams: { percent: INNER_RETRACT_PERCENT }
                    },
                    {
                        key: 'stripR',
                        label: 'Strip R',
                        value: innerStripR,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.stripR'
                    },
                    {
                        key: 'pullOffR',
                        label: 'Pull-off',
                        value: innerPullCombineR,
                        unit: 'mm',
                        tooltipKey: innerPullCombineR >= innerStripR
                            ? 'machineConfig.EW05.inner.pullOffRComplete'
                            : 'machineConfig.EW05.inner.pullOffRPartial'
                    },
                ] : []
            });

            // === OUTER 2: 2° passata (solo se necessaria) ===
            if (hasTwoPass) {
                sections.push({
                    id: 'outer2',
                    label: 'OUTER',
                    sublabel: 'Sguainatura guaina esterna - 2° passata',
                    sublabelKey: 'machineConfig.EW05.sections.outer2',
                    parameters: [
                        // Se lato ha bisogno di split, mettiamo il target totale (o il resto? User ha detto "il resto" ma macchine ragionano in assoluto, metto target finale per sicurezza, o 0 se non serve)
                        // User: "cappando la prima a 150 e il resto sulla seconda ... altrimenti i parametri outer 2 vanno messi a 0"
                        // Interpretazione: Passata 1 = 150. Passata 2 = Target finale (che include il resto).
                        // Se lato NON ha bisogno di split, Passata 2 = 0.
                        // 2° Passata logic:
                        // Se Strip = 0 -> Pull-off = 0
                        // Se Strip > 200 -> Pull-off = 200 (Max macchina)
                        // Altrimenti -> Strip + 5 (Sguainatura completa)
                        {
                            key: 'stripL',
                            label: 'Strip L',
                            value: needsSplitL ? targetStripL : 0,
                            unit: 'mm',
                            tooltipKey: needsSplitL
                                ? 'machineConfig.EW05.outer2.stripL'
                                : 'machineConfig.EW05.outer2.stripLNone'
                        },
                        {
                            key: 'pullOffL',
                            label: 'Pull-off',
                            value: !needsSplitL ? 0 : (targetStripL > 200 ? 200 : targetStripL + 5),
                            unit: 'mm',
                            manual: false,
                            tooltipKey: !needsSplitL
                                ? 'machineConfig.EW05.outer2.pullOffLNone'
                                : targetStripL > 200
                                    ? 'machineConfig.EW05.outer2.pullOffLMax'
                                    : 'machineConfig.EW05.outer2.pullOffLComplete',
                            tooltipParams: !needsSplitL
                                ? undefined
                                : targetStripL > 200
                                    ? { max: 200 }
                                    : undefined
                        },
                        {
                            key: 'stripR',
                            label: 'Strip R',
                            value: needsSplitR ? targetStripR : 0,
                            unit: 'mm',
                            tooltipKey: needsSplitR
                                ? 'machineConfig.EW05.outer2.stripR'
                                : 'machineConfig.EW05.outer2.stripRNone'
                        },
                        {
                            key: 'pullOffR',
                            label: 'Pull-off',
                            value: !needsSplitR ? 0 : (targetStripR > 200 ? 200 : targetStripR + 5),
                            unit: 'mm',
                            manual: false,
                            tooltipKey: !needsSplitR
                                ? 'machineConfig.EW05.outer2.pullOffRNone'
                                : targetStripR > 200
                                    ? 'machineConfig.EW05.outer2.pullOffRMax'
                                    : 'machineConfig.EW05.outer2.pullOffRComplete',
                            tooltipParams: !needsSplitR
                                ? undefined
                                : targetStripR > 200
                                    ? { max: 200 }
                                    : undefined
                        },
                    ]
                });
            }

            return sections;
        }
    }
};

export const DEFAULT_MACHINE = 'EW-05';

/**
 * Ottieni la lista delle macchine disponibili per un dropdown/selezione.
 */
export const getMachineList = () => {
    return Object.values(MACHINES).map(m => ({
        id: m.id,
        name: m.name,
        description: m.description
    }));
};
